import React from 'react';
import { SignUp } from '@clerk/clerk-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

const UserRegistration = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20">
        <div className="min-h-[calc(100vh-5rem)] flex items-center">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8">
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              
              {/* Left Side - Value Proposition */}
              <div className="space-y-8 lg:pr-8">
                <div>
                  <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                    <Icon name="Star" size={16} />
                    <span className="text-sm font-medium">Join 50,000+ Students</span>
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6 leading-tight">
                    Your Journey to
                    <span className="block text-primary">China Starts Here</span>
                  </h1>
                  
                  <p className="text-lg text-muted-foreground font-caption leading-relaxed mb-8">
                    Get instant access to 200+ top Chinese universities and language programs. 
                    Start your application in minutes, not months.
                  </p>
                </div>

                {/* Key Features - Clean & Minimal */}
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon name="CheckCircle" size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                        One Profile, Multiple Applications
                      </h3>
                      <p className="text-sm text-muted-foreground font-caption">
                        Apply to multiple universities without filling forms repeatedly
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon name="Zap" size={24} color="var(--color-accent)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                        Real-Time Application Tracking
                      </h3>
                      <p className="text-sm text-muted-foreground font-caption">
                        Get instant notifications on your application status
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon name="Shield" size={24} color="var(--color-secondary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                        Expert Guidance & Support
                      </h3>
                      <p className="text-sm text-muted-foreground font-caption">
                        24/7 access to education advisors and visa assistance
                      </p>
                    </div>
                  </div>
                </div>

                {/* Student Testimonial */}
                <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-6 border border-primary/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                      M
                    </div>
                    <div>
                      <div className="font-semibold text-foreground">Maurice Lamah</div>
                      <div className="text-sm text-muted-foreground">Guandong University of Science and Technology, Class of 2020</div>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Icon key={i} name="Star" size={16} color="var(--color-warning)" fill="var(--color-warning)" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground font-caption italic">
                    "ChineseLangApp made my application process incredibly smooth. I got accepted 
                    to my dream university within 2 months. The support team was amazing!"
                  </p>
                </div>

                {/* Trust Badges - Minimal */}
                <div className="flex items-center gap-6 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Lock" size={16} color="var(--color-success)" />
                    <span>256-bit SSL</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Shield" size={16} color="var(--color-success)" />
                    <span>GDPR Compliant</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Award" size={16} color="var(--color-success)" />
                    <span>ISO Certified</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Sign Up Form */}
              <div className="relative">
                {/* Decorative Background Elements */}
                <div className="absolute -top-4 -right-4 w-72 h-72 bg-primary/5 rounded-full blur-3xl -z-10"></div>
                <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-accent/5 rounded-full blur-3xl -z-10"></div>
                
                <div className="bg-card rounded-3xl shadow-2xl border border-border overflow-hidden">
                  <div className="p-8 md:p-10">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                        Create Your Account
                      </h2>
                      <p className="text-sm text-muted-foreground font-caption">
                        Join thousands of students pursuing their dreams
                      </p>
                    </div>

                    {/* Clerk Sign Up Component */}
                    <div className="flex items-center justify-center w-full min-h-[500px]">
                      <div className="w-full flex justify-center">
                        <SignUp 
                          signInUrl="/user-login"
                          afterSignUpUrl="/user-dashboard"
                          redirectUrl="/user-dashboard"
                          appearance={{
                            elements: {
                              rootBox: "mx-auto",
                              card: "shadow-none bg-transparent mx-auto",
                              formButtonPrimary: "bg-primary hover:bg-primary/90 text-sm normal-case",
                              formFieldInput: "rounded-lg",
                              footerActionLink: "text-primary hover:text-primary/80"
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bottom Info Bar */}
                  <div className="bg-muted/30 px-8 py-4 border-t border-border">
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Info" size={14} />
                      <span>Free account • No credit card required • Cancel anytime</span>
                    </div>
                  </div>
                </div>

                {/* Help Section */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">Need help?</p>
                  <div className="flex items-center justify-center gap-4">
                    <a href="mailto:support@chineselangapp.com" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                      <Icon name="Mail" size={16} />
                      Email Support
                    </a>
                    <span className="text-muted-foreground">•</span>
                    <a href="tel:+8618810799128" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                      <Icon name="Phone" size={16} />
                      Call Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats Section */}
        <div className="bg-gradient-to-r from-primary/5 via-background to-accent/5 border-t border-border py-12">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2">50K+</div>
                <div className="text-sm text-muted-foreground font-caption">Active Students</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-accent mb-2">200+</div>
                <div className="text-sm text-muted-foreground font-caption">Universities</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-secondary mb-2">95%</div>
                <div className="text-sm text-muted-foreground font-caption">Success Rate</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading font-bold text-success mb-2">120+</div>
                <div className="text-sm text-muted-foreground font-caption">Countries</div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default UserRegistration;